import classNames from 'classnames';
import { observer } from 'mobx-react';
import React from 'react';

export interface IFlexContainerProps
  extends React.HTMLAttributes<Omit<HTMLDivElement, 'children'>> {
  children:
    | React.ReactElement<IFlexContainerColumnProps>
    | React.ReactElement<IFlexContainerColumnProps>[]
    | React.ReactElement<IFlexContainerRowProps>
    | React.ReactElement<IFlexContainerRowProps>;
}

/**
 * FlexContainer is a component intended to standardize using flex layouts, this currently supports a Container which does a d-flex and flex-fill
 * that has children that are either flex-column or flex-row that can shrink or grow.  Knowledge of how bootstrap's flex classes is still needed.
 * For an example, check out PetDetailsDisplay.tsx and PetDetailsForm.tsx
 */
const FlexContainerImp = observer(
  ({ className, children, ...attrs }: IFlexContainerProps) => {
    return (
      <div className={classNames('d-flex flex-fill', className)} {...attrs}>
        {children}
      </div>
    );
  },
);

export interface IFlexContainerColumnProps
  extends React.HTMLAttributes<Omit<HTMLDivElement, 'children'>> {
  fill?: boolean;
  shrink?: boolean;
}

const Column = observer(
  ({
    className,
    shrink,
    fill,
    children,
    ...attrs
  }: IFlexContainerColumnProps) => {
    return (
      <div
        className={classNames(
          className,
          'd-flex flex-column',
          fill ? 'flex-fill' : '',
          shrink ? 'flex-shrink-1' : '',
        )}
        {...attrs}
      >
        {children}
      </div>
    );
  },
);

export interface IFlexContainerRowProps
  extends React.HTMLAttributes<Omit<HTMLDivElement, 'children'>> {
  fill?: boolean;
  shrink?: boolean;
}

const Row = observer(
  ({ className, shrink, fill, children, ...attrs }: IFlexContainerRowProps) => {
    return (
      <div
        className={classNames(
          className,
          'd-flex flex-row',
          fill ? 'flex-fill' : '',
          shrink ? 'flex-shrink-1' : '',
        )}
        {...attrs}
      >
        {children}
      </div>
    );
  },
);

const FlexContainer = Object.assign(FlexContainerImp, {
  Column,
  Row,
});

export { FlexContainer };

export default FlexContainer;
